import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogClose, DialogContent, DialogTitle } from '@codes/ui-kit/components/ui/dialog';
import { Input } from '@codes/ui-kit/components/ui/input';
import { ScrollArea } from '@codes/ui-kit/components/ui/scroll-area';
import { ChevronLeft, FileText, Search, X } from 'lucide-react';

import { useDebounce } from '@codes/shared';

import type { ContentViewWithChildren } from './SideBar';
import type { ContentView } from '@lemoncloud/eureka-contents-api';

interface SearchDialogProps {
    open: boolean;
    contentsWithChildren: ContentViewWithChildren[];
    onOpenChange: (open: boolean) => void;
    onContentSelect: (content: ContentView) => void;
}

const HighlightedText = ({ text, searchTerm }: { text: string; searchTerm: string }) => {
    if (!searchTerm) {
        return <>{text}</>;
    }

    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return (
        <>
            {parts.map((part, i) =>
                part.toLowerCase() === searchTerm.toLowerCase() ? (
                    <mark key={i} className="text-[#0077BF] font-semibold bg-transparent">
                        {part}
                    </mark>
                ) : (
                    part
                )
            )}
        </>
    );
};

interface SearchResult {
    id: string;
    title: string;
    readme?: string;
    parentPath?: {
        title: string;
        id: string;
    }[];
}

export const SearchDialog = ({ open, onOpenChange, onContentSelect, contentsWithChildren }: SearchDialogProps) => {
    const { t } = useTranslation(['translation', 'box']);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 200);

    const filteredResults = useMemo(() => {
        if (!debouncedSearchTerm) {
            return [];
        }

        const searchTermLower = debouncedSearchTerm.toLowerCase();
        const results: SearchResult[] = [];

        const processContent = (content: ContentViewWithChildren, parents: { title: string; id: string }[] = []) => {
            if (
                content.title?.toLowerCase().includes(searchTermLower) ||
                content.readme?.toLowerCase().includes(searchTermLower)
            ) {
                results.push({
                    id: content.id,
                    title: content.title || '',
                    readme: content.readme,
                    parentPath: parents.length > 0 ? parents : undefined,
                });
            }

            content.children?.forEach(child => {
                processContent(child, [...parents, { title: content.title || '', id: content.id }]);
            });
        };

        contentsWithChildren.forEach(content => {
            processContent(content);
        });

        return results;
    }, [contentsWithChildren, debouncedSearchTerm]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogTitle></DialogTitle>
            <DialogContent className="max-w-[650px] p-0" showCloseButton={false}>
                <div className="flex flex-col h-[200px] p-3">
                    <div className="flex items-center gap-[6px]">
                        <DialogClose asChild>
                            <button className="text-text-800">
                                <ChevronLeft />
                            </button>
                        </DialogClose>
                        <div className="w-full h-[37px] bg-[#F4F5F5] dark:bg-[#222325] p-2 rounded-[6px] flex items-center">
                            <Search className="h-4 w-4 text-text" />
                            <Input
                                type="text"
                                placeholder={t('box:search.placeholder')}
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="w-full border-none text-text"
                                autoFocus
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="w-5 h-5 rounded-full bg-[#dfe0e2] flex items-center justify-center"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>
                    <ScrollArea className="flex-grow mt-[18px] overflow-auto">
                        <div className="space-y-1">
                            {filteredResults.map(result => (
                                <div
                                    key={result.id}
                                    onClick={() => {
                                        onContentSelect(result);
                                        onOpenChange(false);
                                    }}
                                    className="flex space-x-2 p-1 rounded-[4px] hover:bg-[#F4F5F5] dark:hover:bg-[#53555B] transition-colors cursor-pointer"
                                >
                                    <FileText className="w-4 h-4 mt-[2px] text-text" />
                                    <div className="flex-1">
                                        <div className="flex items-center gap-1 text-sm">
                                            <h3 className="text-text">
                                                <HighlightedText
                                                    text={result.title || t('box:search.newPage')}
                                                    searchTerm={debouncedSearchTerm}
                                                />
                                            </h3>
                                            {result.parentPath && (
                                                <span className="text-xs text-dim whitespace-nowrap">
                                                    {result.parentPath.map((parent, index) => (
                                                        <span key={parent.id}>
                                                            {index > 0 && ' / '}
                                                            <HighlightedText
                                                                text={parent.title}
                                                                searchTerm={debouncedSearchTerm}
                                                            />
                                                        </span>
                                                    ))}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {filteredResults.length === 0 && debouncedSearchTerm && (
                                <p className="text-center text-text-600 py-[50px]">{t('box:search.noResults')}</p>
                            )}
                        </div>
                    </ScrollArea>
                </div>
            </DialogContent>
        </Dialog>
    );
};
